import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {
    formatDate,
    formatAmountValue,
    shouldShowForeignAmount,
    getTaskOverdueStatus,
    formatDocumentType
} from "utils/valueFormatter.function.js";
import '../pagelayout/global.scss';
import "./my_tasks.scss";
import {TASK_OVERDUE_STATUS, TASK_DETAILS_URL_PART, KEYS} from "utils/constants.js";
import moment from "moment";
import * as Api from '../../utils/api/api';
import {DOCUMENT_TYPE} from "utils/constants.js";
import substitute from "../../assets/24_substitute.svg";
import forwarded from "../../assets/24_forwarded_task.svg";
import review from "../../assets/24_search.svg";
import reassigned from "../../assets/24_reassign.svg";
import {Tooltip} from "../tooltip/Tooltip";
import Checkbox from "components/common/checkbox.component";
import { Button, ButtonType } from "components/common/Button.component";
import { CheckIcon, XIcon } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";

/**
 * see the expected propTypes
 */
export class TaskView extends Component {

    static propTypes = {
        task: PropTypes.object, // json for one task
        selectCallback: PropTypes.func, // callback to un/select task
        approveCallback: PropTypes.func, // callback to approve task
        rejectCallback: PropTypes.func, // callback to reject task
        reviewCallback: PropTypes.func, // callback to review task
        selected: PropTypes.bool, // if the task is selected
        translate: PropTypes.func, // to translate texts
        displayedDate: PropTypes.string, // name of the date to be displayed
        showLastChangedBy: PropTypes.bool // whether to show the lastChangedBy column
    };

    constructor(props) {
        super(props);
        this.state = {
            lastComment: '',
            draftComment: '',
        }
        this.onSelect = this.onSelect.bind(this);
        this.normalizeAmount = this.normalizeAmount.bind(this);
        this.formatTaskDescription = this.formatTaskDescription.bind(this);
        this.getDraftAndLastComment = this.getDraftAndLastComment.bind(this);
        this.displayDraftAndLastComment = this.displayDraftAndLastComment.bind(this);
        this.handleSelectKeyDown = this.handleSelectKeyDown.bind(this);
        this.cropString = this.cropString.bind(this);
    }

    /**
     * hide differences in task detail depending on task type :/
     */
    normalizeAmount(task) {
        let unit, amount, foreignUnit, foreignAmount;

        if (task.documentType === DOCUMENT_TYPE.LEAVE_REQUEST) {
            amount = task.numberOfDays;
            unit = this.props.translate('myTasks.task.amountInDays');
        } else if (task.documentType === DOCUMENT_TYPE.TIMESHEET) {
            amount = task.amount;
            unit = this.props.translate('myTasks.task.amountInHours');
            foreignAmount = task.foreignAmount;
            foreignUnit = unit;
        } else {
            amount = task.amount;
            unit = task.currency;
            foreignAmount = task.foreignAmount;
            foreignUnit = task.foreignCurrency;
        }


        let status = shouldShowForeignAmount(amount, unit, foreignAmount, foreignUnit);

        return {
            amount: formatAmountValue(status.amount),
            unit: status.unit,
            foreignAmount: status.foreignAmount ? formatAmountValue(status.foreignAmount) : null,
            foreignCurrency: status.foreignUnit
        }
    }

    formatTaskDescription(task) {
        let description = {
            text: task.description,
            hoverText: task.description,
        };
        if (task.documentType === DOCUMENT_TYPE.LEAVE_REQUEST || task.documentType === DOCUMENT_TYPE.TIMESHEET) {
            let from = new Date(task.fromDate);
            let to = new Date(task.toDate);
            let descriptionDate = undefined;
            if (from.getYear() === to.getYear()) {
                if (from.getMonth() === to.getMonth()) {
                    //(May 1. - 31.) + description
                    descriptionDate = moment(from).format("MMM D.") + " - " + moment(to).format("D.");
                } else {
                    //(Apr 1. - May 2.) + description
                    descriptionDate = moment(from).format("MMM D.") + " - " + moment(to).format("MMM D.");
                }
            } else {
                //(2016 Apr 1. - 2017 May 2.) + description
                descriptionDate = moment(from).format("YYYY MMM D.") + " - " + moment(to).format("YYYY MMM D.");
            }
            description.text = (<span> {"(" + descriptionDate + ") " + task.description}</span>);
        }
        return description;
    }

    getDraftAndLastComment(hasDraftComment) {
        Api.getLastCommentByUID(this.props.task.uid).then(response => {
            if (response && response.content) {
                this.setState({
                    lastComment: response.userName + "\n" + response.content
                });
            }
        })
        if (hasDraftComment)
            Api.getDraftComment(this.props.task.documentUid).then(response => {
                if (response && response.text) {
                    this.setState({
                        draftComment: response.text
                    });
                }
            })
    }

    cropString(str, maxLength) {
        if (str.length > maxLength) {
            return str.slice(0, maxLength) + '...';
        }
        return str;
    }

    displayDraftAndLastComment() {
        const MAX_LENGTH = 250;
        let result = "";
        if (this.state.lastComment)
            result += this.props.translate('myTasks.task.lastComment') + this.cropString(this.state.lastComment, MAX_LENGTH) + "\n\n";
        if (this.state.draftComment)
            result += this.props.translate('myTasks.task.savedDraft') + "\n" + this.cropString(this.state.draftComment, MAX_LENGTH);
        return result;
    }

    onSelect(e) {
        e.stopPropagation();
        e.preventDefault();
        this.props.selectCallback(this.props.task.key);
    };

    handleSelectKeyDown(event) {
        if (event.keyCode === KEYS.ENTER) {
            this.props.selectCallback(this.props.task.key);
        }
    }

    render() {

        let task = this.props.task;
        let typeClass = formatDocumentType(task.documentType, task.amount);
        let amountFields = this.normalizeAmount(task);

        let status = getTaskOverdueStatus(task.documentDueDate);
        let warningTooltip = status.overdueClass !== TASK_OVERDUE_STATUS.TASK_NOT_OVERDUE ?
            this.props.translate('myTasks.task.taskOverdue', status.dueDate, formatDate(task.dueDate)) : '';

        let getOriginalAssignee = task.originalAssignee ? (this.props.translate("myTasks.task.substitutingFor") + " " + task.originalAssignee) : '';

        let taskClass = "col-md-12 container list-group-item  banner-list " + status.overdueClass + (this.props.selected ? ' selected-row' : '') + (task.isNew ? ' new-task' : '');
        if (task.isHandled) {
            taskClass += " task-handled";
        }

        let fromField = task.supplierName ? task.supplierName : task.requesterName || (task.requesterFirstName ? task.requesterFirstName + " " + task.requesterLastName : '');
        let postponed = task.postponedComment ? this.props.translate("myTasks.task.postponed") : "";
        let taskDescription = this.formatTaskDescription(task);

        let inputClickSelect = (event) => {
            event.stopPropagation();
            this.props.selectCallback(this.props.task.key);
        };

        const reviewRow = this.props.reviewCallback !== undefined;

        const showQuickApprove = task.taskActions?.includes("quick-approve");
        const showQuickReject = task.taskActions?.includes("quick-reject");

        return (
            <li key={task.id} className={taskClass} data-cy="mytasks.task">
                <Link to={TASK_DETAILS_URL_PART + task.key}
                      aria-disabled={task.isHandled}
                      tabIndex={task.isHandled ? -1 : 0}
                      onClick={(e) => { if (task.isHandled) e.preventDefault(); }}
                      className="ast_task_link task-nav-link text-decoration-none row w-100 float-left mx-0 px-0 d-flex flex-row justify-content-between">

                    <div className={"task-checkbox text-center float-left ml-0" + (task.isHandled ? " pe-none" : " ")}
                         title={this.props.translate("myTasks.task.select")}
                         onClick={this.onSelect} onKeyDown={this.handleSelectKeyDown}>
                        <Checkbox
                            inputId={"task-checkbox-" + task.id}
                            onChange={inputClickSelect}
                            isChecked={this.props.selected}
                            isDisabled={task.isHandled}
                            dataCy="myTasks.task.select"
                            ariaLabel={this.props.translate("myTasks.task.selectTaskOf", DOCUMENT_TYPE.asString(task.documentType), fromField) + amountFields.amount + amountFields.unit}
                        />

                    </div>
                    <div className="document-type-column text-center px-0">
                            <span
                                title={this.props.translate("documentType." + DOCUMENT_TYPE.asString(task.documentType))}
                                className={typeClass}
                                data-cy="myTasks.task.type"/>
                    </div>

                    <div className="supplier-name-column text-start px-0">
                        <div className="requested font-16 wrap-multiple-lines"
                             data-cy="myTasks.task.supplierName">{fromField}</div>
                        <span
                            className="hidden-default">{this.props.translate("myTasks.column.supplierName")}</span>
                    </div>
                    <div
                        className="text-start ps-0 pe-3 description-column">
                        <div className="description wrap-multiple-lines"
                             title={taskDescription.hoverText}
                             data-cy="myTasks.task.description">{taskDescription.text}</div>
                        <span
                            className="hidden-default">{this.props.translate("myTasks.column.description")}</span>
                    </div>
                    <div className="company-name-column text-start px-0">
                        <div className={`companyName ${task.branch ? "two-rows mb-2" : ""}`}
                             data-cy="myTasks.task.companyName">{task.companyName}</div>
                        <span className="hidden-default">{this.props.translate("myTasks.column.companyName")}</span>
                        {task.branch ?
                            <span className="branch-name" data-tip data-for={"branchName" + task.id}>
                                    <span className="text-disabled">{task.branch.name}</span>
                                    <Tooltip
                                        id={"branchName" + task.id}
                                        content={() => <div className="text-center">{task.branch.name}</div>}
                                    />
                                </span> : null
                        }

                    </div>
                    <div title={warningTooltip} className="row document-due-column text-start px-0 mx-0">
                            <span className="col-md-12 dueDate px-0 d-flex align-items-center"
                                  data-cy="myTasks.task.documentDueDate">{formatDate(task[this.props.displayedDate])}
                                {status.overdueClass === TASK_OVERDUE_STATUS.TASK_OVERDUE &&
                                    <span className="vismaicon vismaicon-sm vismaicon-filled vismaicon-error ps-1"
                                          alt={warningTooltip}/>
                                }
                            </span>
                        {task.postponedComment &&
                            <span className="col-md-12 px-0">{postponed}</span>
                        }
                        <span
                            className="hidden-default">{this.props.translate("myTasks.column.documentDueDate")}</span>
                    </div>
                    <div className="amount-column text-end px-0">
                            <span className="text-start px-0">
                                <span
                                    className={"font-16 amount " + (task.amount < 0 ? "text-danger" : "")}
                                    data-cy="myTasks.task.amount">
                                    {amountFields.amount}
                                    </span>
                                 <span className="text-start px-0 currency font-16"
                                       data-cy="myTasks.task.currency">
                                     {amountFields.unit}
                                     </span>
                            </span>
                        {amountFields.foreignAmount &&
                            <span><br/>
                                <span className="text-disabled foreign-amount">{amountFields.foreignAmount}</span>
                                <span
                                    className="text-disabled foreign-currency currency text-start">{amountFields.foreignCurrency}</span>
                            </span>
                        }

                        <span className="hidden-default">{this.props.translate("myTasks.column.amount")}</span>
                    </div>

                    {this.props.showLastChangedBy &&
                        <div className="last-changed-by-column text-start px-4">
                            <div data-cy="myTasks.task.lastChangedBy">
                                {task.lastChangedByUserName || ''}
                            </div>
                            <span className="hidden-default">{this.props.translate("myTasks.column.lastChangedBy")}</span>
                        </div>
                    }

                    <div className="comments-column d-flex flex-row"
                         title={this.displayDraftAndLastComment()}
                         onMouseEnter={() => this.getDraftAndLastComment(task.hasDraftComment)}>
                        {(task.numberOfComments > 0 || task.hasDraftComment) &&
                            <div
                                className="ga-badge ga-badge--text ga-badge--default comments-number ml-10"
                                data-cy="myTasks.task.comment">{task.numberOfComments > 0 ? task.numberOfComments : ""}
                                {task.hasDraftComment ?
                                    <div
                                        className={`${task.numberOfComments > 0 ? "ml-8 pe-2 align-text-bottom" : ""} vismaicon vismaicon-sm vismaicon-white-bg vismaicon-edit vismaicon-dynamic`}/> : null}
                            </div>
                        }
                        <span className="hidden-default">{this.props.translate("myTasks.task.comment")}</span>
                    </div>

                    {task.isHandled ?
                        <div className="task-actions float-right pt-3 ps-4">
                            <div className="w-100 mt-3 ps-4 text-start">
                                <span
                                    data-tip=""
                                    data-for={"task-handled-tool-" + task.id}
                                    className="handled-info ms-3 vismaicon vismaicon-dynamic vismaicon-info"/>
                                <Tooltip
                                    id={"task-handled-tool-" + task.id}
                                    content={() => {
                                        return <span
                                            className={"d-block"}>{this.props.translate("myTasks.taskWasHandled")}</span>;
                                    }}/>
                            </div>
                        </div> :

                        <div className="task-actions d-flex justify-content-end align-items-center flex-wrap px-0 gap-8">
                            <div className="d-flex gap-8">
                                {task.actorType === "SUBSTITUTE" &&
                                    <div className="ms-2 ms-xl-3"
                                         title={getOriginalAssignee}>
                                        <img src={substitute} alt="substitute"/>
                                    </div>}

                                {!reviewRow && task.taskAssignmentType === "REVIEW" &&
                                    <div className="ms-2 ms-xl-3"
                                         title={this.props.translate("myTasks.taskIcon.review")}>
                                        <img src={review} alt="review"/>
                                    </div>}

                                {task.taskAssignmentType === "FORWARDED" &&
                                    <div className="ms-2 ms-xl-3"
                                         title={this.props.translate("myTasks.taskIcon.forwarded")}>
                                        <img src={forwarded} alt="forwarded"/>
                                    </div>}

                                {task.taskAssignmentType === "REASSIGNED" &&
                                    <div className="ms-2 ms-xl-3"
                                         title={this.props.translate("myTasks.taskIcon.reassigned")}>
                                        <img src={reassigned} alt="reassigned"/>
                                    </div>}
                            </div>
                            <div className="d-flex gap-8">
                                {!reviewRow && showQuickApprove &&
                                    <Button
                                        type={ButtonType.GHOST}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this.props.approveCallback();
                                        }}
                                        iconOnly={true}
                                        icon={<CheckIcon size={ICON_SIZE.MEDIUM}/>}
                                        title={this.props.translate("myTasks.task.approve")}
                                        dataCy={"task.action." + this.props.translate("myTasks.task.approve")}
                                    />
                                }
                                {!reviewRow && showQuickReject &&
                                    <Button
                                        type={ButtonType.GHOST}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this.props.rejectCallback();
                                        }}
                                        iconOnly={true}
                                        icon={<XIcon size={ICON_SIZE.MEDIUM}/>}
                                        title={this.props.translate("myTasks.task.reject")}
                                        dataCy={"task.action." + this.props.translate("myTasks.task.reject")}
                            />
                                }
                            </div>

                            {reviewRow &&
                                <Button
                                    type={ButtonType.SECONDARY}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.props.reviewCallback();
                                    }}
                                    dataCy={"task.action." + this.props.translate("myTasks.actionBar.review")}
                                    text={this.props.translate("myTasks.actionBar.review")}
                                />
                            }

                        </div>
                    }

                </Link>
            </li>
        )
    }
}

// we need this syntax to make the common.js import working from route definitions (common.js import is mandatory inside bundle split)
export default TaskView;
